const paymentMethodCatalogById = new Map<number, string>();

export const registerPaymentMethodCatalog = (
  methods: ReadonlyArray<{ id: number; name: string }>,
): void => {
  paymentMethodCatalogById.clear();
  for (const method of methods) {
    paymentMethodCatalogById.set(method.id, method.name);
  }
};

export type PaymentMethodLabelInput =
  | string
  | number
  | { id: number; name?: string | null }
  | null
  | undefined;

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  Dinheiro: 'Dinheiro',
  Cheque: 'Cheque',
  CartaoDeCredito: 'Cartão de crédito',
  CartaoDeDebito: 'Cartão de débito',
  BoletoBancario: 'Boleto bancário',
  DepositoBancario: 'Depósito bancário',
  PagamentoInstantaneoPix: 'PIX',
  TransferenciaBancariaCarteiraDigital: 'Transferência bancária, carteira digital',
  PagamentoInstantaneoPixEstatico: 'PIX estático',
  SemPagamento: 'Sem pagamento',
  Outros: 'Outros',
};

const DISPLAY_LABELS = new Set(Object.values(PAYMENT_METHOD_LABELS));

const stripDiacritics = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const compactName = (value: string): string =>
  value.trim().replace(/[\s_-]+/g, '');

const resolvePaymentMethodKey = (name: string): string | null => {
  const trimmed = name.trim();
  if (PAYMENT_METHOD_LABELS[trimmed]) return trimmed;

  const compact = compactName(trimmed);
  if (PAYMENT_METHOD_LABELS[compact]) return compact;

  const insensitive = Object.keys(PAYMENT_METHOD_LABELS).find(
    (key) => key.toLowerCase() === compact.toLowerCase(),
  );
  if (insensitive) return insensitive;

  const normalizedInput = stripDiacritics(compact).toLowerCase();
  const accentInsensitive = Object.keys(PAYMENT_METHOD_LABELS).find(
    (key) => stripDiacritics(key).toLowerCase() === normalizedInput,
  );
  if (accentInsensitive) return accentInsensitive;

  const fromCamelCase = compactName(
    trimmed.replace(/([a-z])([A-Z])/g, '$1 $2'),
  );
  if (PAYMENT_METHOD_LABELS[fromCamelCase]) return fromCamelCase;

  return null;
};

const ACCENTED_WORDS: Record<string, string> = {
  cartao: 'Cartão',
  credito: 'crédito',
  debito: 'débito',
  bancario: 'bancário',
  bancaria: 'bancária',
  deposito: 'Depósito',
  transferencia: 'Transferência',
  carteira: 'carteira',
  digital: 'digital',
  instantaneo: 'instantâneo',
  estatico: 'estático',
  pagamento: 'pagamento',
  sem: 'Sem',
  cheque: 'Cheque',
  dinheiro: 'Dinheiro',
  outros: 'Outros',
  pix: 'PIX',
};

const formatFallbackLabel = (name: string): string => {
  const spaced = name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();

  if (!spaced) return name;

  return spaced
    .split(/\s+/)
    .map((word, index) => {
      const lower = word.toLowerCase();
      const accented = ACCENTED_WORDS[lower];
      if (!accented) return word;
      if (lower === 'pix') return accented;
      if (index === 0 && accented[0] === accented[0].toLowerCase()) {
        return accented[0].toUpperCase() + accented.slice(1);
      }
      return accented;
    })
    .join(' ');
};

export const paymentMethodLabel = (name: string | null | undefined): string => {
  if (!name) return '—';

  const trimmed = name.trim();
  const key = resolvePaymentMethodKey(trimmed);
  if (key) return PAYMENT_METHOD_LABELS[key];

  if (DISPLAY_LABELS.has(trimmed)) return trimmed;

  const displayMatch = [...DISPLAY_LABELS].find(
    (label) => label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (displayMatch) return displayMatch;

  return formatFallbackLabel(trimmed);
};

export const resolvePaymentMethodLabel = (
  input: PaymentMethodLabelInput,
): string => {
  if (input == null) return '—';
  if (typeof input === 'string') return paymentMethodLabel(input);
  if (typeof input === 'object') {
    if (input.name) return paymentMethodLabel(input.name);
    const catalogName = paymentMethodCatalogById.get(input.id);
    if (catalogName) return paymentMethodLabel(catalogName);
    return `#${input.id}`;
  }
  const catalogName = paymentMethodCatalogById.get(input);
  if (catalogName) return paymentMethodLabel(catalogName);
  return `#${input}`;
};
